import { api, enviarFormulario } from '../api';
import { LIMITE_POR_ENVIO } from '../Fotos';

/**
 * As chamadas do módulo de resiliência, com os caminhos escritos por
 * extenso — o teste `26` precisa enxergar a string literal.
 */

export interface EnsaioMr {
  cabecalho: any;
  corpos: any[];
  ciclos: any[];
  arquivos: any[];
  equipamentos: any[];
  fotos: any[];
  /** A balança das massas dos CPs (RN-215, onda 238). */
  balanca?: { id: string; codigo: string; nome: string } | null;
  pendencias: string[];
  pendenciasDetalhadas?: any[];
}

export interface ConferenciaMr {
  simulacao?: boolean;
  importado?: boolean;
  arquivo: string;
  bytes: number;
  aba: string;
  linhasLidas: number;
  corpos: {
    chave: string;
    numeroTeste: number | null;
    identificacao: string;
    diametroMm: number | null;
    alturaMm: number | null;
    ciclos: number;
    tensaoDesvioMinMpa: number;
    tensaoDesvioMaxMpa: number;
  }[];
  ciclosImportados?: number;
  cpsImportados?: number;
}

export const verMr = (id: string) => api<EnsaioMr>(`/ensaios/${id}/mr`);

export const gravarMrCabecalho = (id: string, corpo: unknown) =>
  api<EnsaioMr>(`/ensaios/${id}/mr`, { metodo: 'PUT', corpo });

export const gravarMrCorpos = (id: string, corpo: unknown) =>
  api<EnsaioMr>(`/ensaios/${id}/mr/corpos`, { metodo: 'POST', corpo });

export const gravarMrCiclos = (id: string, cpId: string, corpo: unknown) =>
  api<EnsaioMr>(`/ensaios/${id}/mr/corpos/${cpId}/ciclos`,
    { metodo: 'POST', corpo });

export const descartarMrCiclo = (id: string, cicloId: string, motivo: string) =>
  api<EnsaioMr>(`/ensaios/${id}/mr/ciclos/${cicloId}/descartar`,
    { metodo: 'POST', corpo: { motivo } });

/**
 * Importa o bruto da prensa dinâmica.
 *
 * `testes` é a lista de chaves `número|identificação` dos corpos de
 * prova escolhidos. Vazia, entra o arquivo inteiro — e um arquivo do dia
 * costuma trazer a obra do vizinho junto, por isso a tela simula antes.
 */
export function importarBrutoMr(
  id: string,
  arquivo: File,
  opcoes: { simular?: boolean; testes?: string[] } = {},
  aoProgredir?: (f: number) => void,
): Promise<ConferenciaMr> {
  const form = new FormData();
  form.append('arquivo', arquivo);
  if (opcoes.simular) form.append('simular', 'true');
  if (opcoes.testes?.length) form.append('testes', opcoes.testes.join(','));
  return enviarFormulario<ConferenciaMr>(
    `/ensaios/${id}/mr/importar`, form, aoProgredir);
}

export type TipoFotoMr = 'cp_antes' | 'cp_depois' | 'moldagem' | 'montagem'
  | 'ruptura' | 'equipamento' | 'outro';

/**
 * Sobe as fotos do corpo de prova.
 *
 * `corpoProvaOrdem` é o número do CP dentro do ensaio — o mesmo que sai
 * impresso na folha. Sem ele, a foto vai para a folha do CP 1 e o laudo
 * diz, sem querer, que ela é de lá.
 */
export async function enviarFotosMr(
  id: string,
  fotos: {
    arquivo: File; tipo: TipoFotoMr; legenda?: string;
    corpoProvaOrdem?: number;
  }[],
  aoProgredir?: (f: number) => void,
): Promise<void> {
  /*
   * Em levas de `LIMITE_POR_ENVIO`.
   *
   * O servidor recusa a chamada INTEIRA a partir da décima primeira
   * foto, e recusa em inglês — nenhuma das fotos sobe e a mensagem não
   * diz por quê. Com seis corpos de prova e o par de cada um, doze fotos
   * numa tacada é o caso normal deste ensaio, não a exceção.
   */
  const blocos: typeof fotos[] = [];
  for (let i = 0; i < fotos.length; i += LIMITE_POR_ENVIO) {
    blocos.push(fotos.slice(i, i + LIMITE_POR_ENVIO));
  }

  for (let b = 0; b < blocos.length; b += 1) {
    const form = new FormData();
    for (const f of blocos[b]) form.append('fotos', f.arquivo, f.arquivo.name);
    form.append('itens', JSON.stringify(blocos[b].map((f) => ({
      tipo: f.tipo,
      legenda: f.legenda || undefined,
      corpoProvaOrdem: f.corpoProvaOrdem,
    }))));
    // eslint-disable-next-line no-await-in-loop
    await enviarFormulario(`/ensaios/${id}/mr/fotos`, form,
      (fr) => aoProgredir?.((b + fr) / blocos.length));
  }
}

export const desconsiderarFotoMr = (id: string, fid: string, motivo: string) =>
  api<EnsaioMr>(`/ensaios/${id}/mr/fotos/${fid}/desconsiderar`,
    { metodo: 'POST', corpo: { motivo } });
